import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { erpRegistriesService } from '../../../api/erpRegistriesService';
import { erpEndpointsService } from '../../../api/erpEndpointsService';
import { ERP_ENVIRONMENT_LABELS } from '../../../configs/erpEnvironment';

export function useErpSelect(selectedRegistrySlug?: string) {
  const {
    data: registriesData,
    isLoading: isLoadingRegistries,
  } = useQuery({
    queryKey: ['admin', 'erp', 'registries'],
    queryFn: () => erpRegistriesService.list(),
  });

  const activeRegistries = useMemo(
    () => registriesData?.data.filter((r) => r.is_active) ?? [],
    [registriesData],
  );

  const registries = useMemo(
    () =>
      activeRegistries.map((r) => ({
        value: r.slug,
        // Suffix the environment so workflow authors can tell prod from test.
        label: r.environment
          ? `${r.name} (${ERP_ENVIRONMENT_LABELS[r.environment]})`
          : r.name,
      })),
    [activeRegistries],
  );

  const {
    data: endpointsData,
    isLoading: isLoadingEndpoints,
  } = useQuery({
    queryKey: ['admin', 'erp', 'registries', selectedRegistrySlug, 'endpoints'],
    queryFn: () => erpEndpointsService.list(selectedRegistrySlug!),
    enabled: !!selectedRegistrySlug,
  });

  const endpoints = useMemo(
    () => endpointsData?.data.map((e) => ({ value: e.slug, label: e.name })) ?? [],
    [endpointsData],
  );

  return {
    registries,
    endpoints,
    isLoadingRegistries,
    isLoadingEndpoints: !!selectedRegistrySlug && isLoadingEndpoints,
  };
}
